<script lang="ts" setup>
const visible = defineModel<boolean>('visible', { required: true });

const { t } = useI18n();
const route = useRoute();
const store = useMainStore();
const infoStore = useInfoStore();

const signInRedirectPath = computed(() => {
  const url = new URLSearchParams(route.query as Record<string, string>);
  url.set('action', 'paired-game');
  // TODO: метод лишний, или если и использовать, то нужно encode
  return decodeURIComponent(`${route.path}?${url.toString()}`);
});

const inviteToPairedGame = () => {
  infoStore.inviteToPairedGame();
  visible.value = false;
};
// TODO: тут не оптимальная верстка, не надо дублировать модалки
</script>

<template>
  <Dialog
    v-if="!store.isAuthorized"
    dismissable-mask
    :base-z-index="5"
    :draggable="false"
    modal
    v-model:visible="visible"
    class="w-128"
  >
    <template #header>
      {{ t('title') }}
    </template>

    <p class="m-0 leading-6">
      {{ t('should_sign_in') }}
    </p>

    <template #footer>
      <Button
        @click="visible = false"
        :label="t('close')"
        icon="pi pi-times"
        text
      />
      <Button
        as="nuxt-link"
        :to="{
          path: '/auth/sign-in',
          query: { authRedirect: signInRedirectPath },
        }"
        :label="t('sign_in')"
        icon="pi pi-sign-in"
        text
      />
    </template>
  </Dialog>

  <Dialog
    v-else-if="!store.user.pairedUser"
    style="width: 520px"
    dismissable-mask
    :base-z-index="5"
    :draggable="false"
    modal
    v-model:visible="visible"
    class="w-128"
  >
    <template #header>
      {{ t('title') }}
    </template>

    <p class="m-0 leading-6">
      {{ t('need_partner') }}
    </p>

    <template #footer>
      <Button
        @click="visible = false"
        :label="t('close')"
        icon="pi pi-times"
        text
      />
      <Button
        as="nuxt-link"
        :to="{ path: '/profile' }"
        :label="t('profile')"
        icon="pi pi-user"
        text
      />
    </template>
  </Dialog>

  <Dialog
    v-else
    dismissable-mask
    :base-z-index="5"
    :draggable="false"
    modal
    v-model:visible="visible"
    class="w-128"
  >
    <template #header>
      {{ t('title') }}
    </template>

    <div class="flex flex-col items-center">
      <p class="m-0 leading-6">
        {{ t('start_description') }}
      </p>

      <NuxtImg
        src="/images/instructions/paired-game.png"
        width="1024"
        height="689"
        format="webp"
        loading="lazy"
        class="w-full my-5 block h-auto"
      />

      <Button
        v-if="!store.pro"
        @click="store.isSubscriptionDialogVisible = true"
        :label="t('unlock')"
        class="w-full"
      />
      <Button
        v-else
        @click="inviteToPairedGame"
        :label="t('start')"
        rounded
        size="large"
        class="px-8 max-w-full"
      />
    </div>
  </Dialog>
</template>

<i18n>
en:
  close: Close
  sign_in: Sign in
  profile: Profile
  start: Start
  title: Playing on two devices
  should_sign_in: To play on two devices with your partner, you must log in.
  need_partner: To play from two devices, you must add a partner in your profile settings.
  start_description: Send an invitation to your partner to start the game from two devices. This will increase the
    intrigue of each task, since you will not know your partner's task until the very moment of its completion.
  unlock: Unlock.

ru:
  close: Закрыть
  sign_in: Войти
  profile: Профиль
  start: Начать
  title: Игра с двух устройств
  should_sign_in: Для игры с двух устройств с вашим партнером необходимо авторизоваться.
  need_partner: Для игры с двух устройств необходимо добавить партнера в настройках профиля.
  start_description: Отправить приглашение партнеру для начала игры с двух устройств. Это усилит интригу от каждого
    задания, т.к. вы не будете знать задание партнера до самого момента его исполнения.
  unlock: Разблокировать

de:
  close: Schließen
  sign_in: Login
  profile: Profil
  start: Beginnen
  title: Abspielen von zwei Geräten
  should_sign_in: Um mit Ihrem Partner von zwei Geräten aus zu spielen, müssen Sie sich anmelden.
  need_partner: Um von zwei Geräten aus zu spielen, müssen Sie in Ihren Profileinstellungen einen Partner hinzufügen.
  start_description: Senden Sie eine Einladung an Ihren Partner, mit dem Spielen auf zwei Geräten zu beginnen. Dies erhöht
    die Faszination jeder Aufgabe, denn... Sie werden die Aufgabe Ihres Partners erst in dem Moment kennen, in dem er
    ausgeführt wird.
  unlock: Entsperren.

es:
  close: Cerca
  sign_in: Acceso
  profile: Perfil
  start: Comenzar
  title: Jugar desde dos dispositivos
  should_sign_in: Para jugar desde dos dispositivos con tu pareja, debes iniciar sesión.
  need_partner: Para jugar desde dos dispositivos, debes agregar un socio en la configuración de tu perfil.
  start_description: Envía una invitación a tu compañero para comenzar a jugar desde dos dispositivos. Esto aumentará la
    intriga de cada tarea, porque... no conocerás la tarea de tu compañero hasta el mismo momento de su ejecución.
  unlock: Desbloquear.

fr:
  close: Fermer
  sign_in: Se connecter
  profile: Profil
  start: Commencer
  title: Jouer à partir de deux appareils
  should_sign_in: Pour jouer depuis deux appareils avec votre partenaire, vous devez vous connecter.
  need_partner: Pour jouer à partir de deux appareils, vous devez ajouter un partenaire dans les paramètres de votre profil.
  start_description: Envoyez une invitation à votre partenaire pour commencer à jouer depuis deux appareils. Cela
    augmentera l'intrigue de chaque tâche, car... vous ne connaîtrez la tâche de votre partenaire qu’au moment même de
    son exécution.
  unlock: Débloquer.
</i18n>
